import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router';

// MUI
import Paper from '@mui/material/Paper';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import Switch from '@mui/material/Switch';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import dayjs, { type Dayjs } from 'dayjs';
import { ArrowLeft, Save } from 'lucide';

// common
import Icon from 'common/components/Icon';
import useUserRoles from 'common/hook/useUserRoles';

// screen
import useGetConfirmationCodes from 'modules/confirmation-code/services/useGetConfirmationCodes';
import useUpdateConfirmationCode from 'modules/confirmation-code/services/useUpdateConfirmationCode';
import useConfirmationClients from 'modules/confirmation-code/services/useConfirmationClients';
import { CONFIRMATION_CODE_STATUS } from 'modules/confirmation-code/types';

const labelSx = { fontSize: 14, color: 'text.primary', mb: 0.5 } as const;

const isoToDayjs = (iso?: string | null): Dayjs | null => (iso ? dayjs(iso) : null);

const ConfirmationCodeDetailPage = () => {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const { data: codes = [], isLoading } = useGetConfirmationCodes({});
  const { mutateAsync, isPending } = useUpdateConfirmationCode();
  const { nameOf } = useConfirmationClients();
  // Chỉ CS-BAD được sửa; Nutri-BAD/SAD/RAD chỉ List/Read (xlsx sheet 03 + grant BE) →
  // khoá các trường và ẩn nút Lưu thay vì để bấm rồi nhận 403.
  const { isCSBAD: canWrite } = useUserRoles();

  const current = codes.find((c) => c.id === id);

  const [active, setActive] = useState(true);
  const [accessLimit, setAccessLimit] = useState(0);
  const [expiredDate, setExpiredDate] = useState<Dayjs | null>(null);
  const [description, setDescription] = useState('');

  useEffect(() => {
    if (current) {
      setActive(current.status === CONFIRMATION_CODE_STATUS.ACTIVE);
      setAccessLimit(current.accessLimit);
      setExpiredDate(isoToDayjs(current.expiredAt));
      setDescription(current.description ?? '');
    }
  }, [current]);

  // Từ /:id đi lên 1 cấp là về list. KHÔNG nối thêm 'confirmation-codes'
  // (sẽ khớp lại route ':id' → màn "không tìm thấy").
  const backToList = () => navigate('..');

  const handleSave = async () => {
    if (!id) return;
    try {
      await mutateAsync({
        id,
        body: {
          expiredAt: expiredDate?.isValid() ? expiredDate.endOf('day').toISOString() : undefined,
          status: active ? CONFIRMATION_CODE_STATUS.ACTIVE : CONFIRMATION_CODE_STATUS.DISABLED,
          accessLimit: Number(accessLimit),
          description,
        },
      });
      backToList();
    } catch {
      /* snackbar đã báo trong hook */
    }
  };

  if (!isLoading && !current) {
    return (
      <Paper sx={{ p: 3 }}>
        <Typography>Không tìm thấy mã xác nhận.</Typography>
        <Button sx={{ mt: 2 }} startIcon={<Icon icon={ArrowLeft} size={16} />} onClick={backToList}>
          Danh sách
        </Button>
      </Paper>
    );
  }

  return (
    <Paper sx={{ p: 3 }}>
      {/* Không có header trong card: Figma dùng tiêu đề trang "SỬA MÃ XÁC NHẬN" + breadcrumb
          do layout tự render (routes: confirmationCode.detail). */}
      {/* 2 cột như Figma — dùng breakpoint 'tablet' của theme ('md' không tồn tại, xem
          setupTheme.ts) nếu không sẽ rơi về 1 cột. */}
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: { xs: '1fr', tablet: '1fr 1fr' },
          columnGap: 4,
          rowGap: 2.5,
          // KHÔNG giới hạn bề rộng: Figma cho 2 cột trải hết chiều rộng card.
        }}
      >
        {/* Cột trái */}
        <Box>
          <Typography sx={labelSx}>Mã code:</Typography>
          <TextField fullWidth size="small" value={current?.code ?? ''} InputProps={{ readOnly: true }} disabled />
        </Box>
        <Box>
          <Typography sx={labelSx}>
            Số lượt truy cập: <Box component="span" sx={{ color: 'error.main' }}>*</Box>
          </Typography>
          <TextField
            fullWidth
            size="small"
            type="number"
            inputProps={{ min: 1 }}
            value={accessLimit}
            onChange={(e) => setAccessLimit(Number(e.target.value))}
            disabled={!canWrite}
          />
        </Box>

        <Box>
          <Typography sx={labelSx}>Tên trường học:</Typography>
          <TextField
            fullWidth
            size="small"
            value={nameOf(current?.targetOrgId)}
            InputProps={{ readOnly: true }}
            disabled
          />
        </Box>
        <Box>
          <Typography sx={labelSx}>Hạn sử dụng:</Typography>
          <DatePicker
            value={expiredDate}
            onChange={(v) => setExpiredDate(v)}
            format="DD/MM/YYYY"
            disabled={!canWrite}
            slotProps={{ textField: { size: 'small', fullWidth: true } }}
          />
        </Box>

        <Box>
          <Typography sx={labelSx}>
            Trạng thái quản trị: <Box component="span" sx={{ color: 'error.main' }}>*</Box>
          </Typography>
          <Switch
            checked={active}
            onChange={(e) => setActive(e.target.checked)}
            color="success"
            disabled={!canWrite}
          />
        </Box>
        <Box>
          <Typography sx={labelSx}>Ghi chú</Typography>
          <TextField
            fullWidth
            size="small"
            multiline
            minRows={8}
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            disabled={!canWrite}
          />
        </Box>
      </Box>

      <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1.5, mt: 3 }}>
        <Button variant="outlined" color="primary" startIcon={<Icon icon={ArrowLeft} size={16} />} onClick={backToList}>
          Quay lại
        </Button>
        {/* Chỉ CS-BAD được sửa (xlsx sheet 02 + grant BE) — vai trò khác chỉ xem. */}
        {canWrite && (
          <Button
            variant="contained"
            color="primary"
            startIcon={<Icon icon={Save} size={16} />}
            onClick={handleSave}
            disabled={isPending}
          >
            Lưu dữ liệu
          </Button>
        )}
      </Box>
    </Paper>
  );
};

export default ConfirmationCodeDetailPage;
